import { SummaryDetailBox } from "./SummaryDetailBox";
import { SummaryDetailMetric } from "./SummaryDetailMetric";

type Theme = "red" | "green" | "amber" | "default";

interface PipelineStatistics {
  leadsToday?: number | null;
  leadsTotal?: number;
  predictedToDocs: number;
  docsToday: number;
  totalReDoc?: number | null;
  gapDocsOut: number;
  gapReadyToGo: number;
  gapPayout: number;
  gapTotal: number;
  predictedToPay: number;
  paidToday: number;
}

interface TopLevelStatsBoxProps {
  statistics: PipelineStatistics | null;
}

const THEMES = {
  RED: "red" as const,
  GREEN: "green" as const,
  AMBER: "amber" as const,
  DEFAULT: "default" as const,
} as const;

const getTodayTheme = (today: number | undefined, predicted: number | undefined): Theme => {
  if (!today || !predicted) return THEMES.DEFAULT;

  if (today > predicted) return THEMES.GREEN;
  if (today < predicted) return THEMES.AMBER;
  return THEMES.DEFAULT;
};

const getPredictionTheme = (predicted: number | undefined): Theme => {
  return predicted === 0 ? THEMES.RED : THEMES.DEFAULT;
};

export const TopLevelStatsBox = ({ statistics }: TopLevelStatsBoxProps) => {
  const { predictedToDocs, docsToday, predictedToPay, paidToday } = statistics || {};

  const predictedToDocsTheme = getPredictionTheme(predictedToDocs);
  const predictedToPayTheme = getPredictionTheme(predictedToPay);
  const docsTodayTheme = getTodayTheme(docsToday, predictedToDocs);
  const paidTodayTheme = getTodayTheme(paidToday, predictedToPay);

  return (
    <div className="grid gap-3 border-b border-solid border-slate-200 bg-slate-50 p-3 pt-5 md:grid-cols-2 xl:grid-cols-5">
      {statistics?.leadsToday !== undefined && statistics?.leadsToday !== null && (
        <SummaryDetailBox label="Leads">
          <SummaryDetailMetric label="Today" value={statistics?.leadsToday} />
        </SummaryDetailBox>
      )}

      <SummaryDetailBox
        label="Docs"
        colSpan={statistics?.leadsToday !== undefined && statistics?.leadsToday !== null ? 1 : 2}
      >
        <SummaryDetailMetric label="Predicted" value={statistics?.predictedToDocs} theme={predictedToDocsTheme} />
        <SummaryDetailMetric label="Today" value={statistics?.docsToday} theme={docsTodayTheme} />
        {statistics?.totalReDoc !== undefined && statistics?.totalReDoc !== null && (
          <SummaryDetailMetric label="Redocs" value={statistics?.totalReDoc} />
        )}
      </SummaryDetailBox>

      <SummaryDetailBox label="Gap" colSpan={2}>
        <SummaryDetailMetric label="WIP" value={statistics?.gapDocsOut} />
        <SummaryDetailMetric label="Ready to Go" value={statistics?.gapReadyToGo} />
        <SummaryDetailMetric label="Payout" value={statistics?.gapPayout} />
        <SummaryDetailMetric label="Total" value={statistics?.gapTotal} />
      </SummaryDetailBox>

      <SummaryDetailBox label="Paid">
        <SummaryDetailMetric label="Predicted" value={statistics?.predictedToPay} theme={predictedToPayTheme} />
        <SummaryDetailMetric label="Today" value={statistics?.paidToday} theme={paidTodayTheme} />
      </SummaryDetailBox>
    </div>
  );
};
